import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError } from "@/app/api";
import type { AuditEntry } from "@/app/audit";
import { useCanWrite } from "@/app/auth";
import { TextField } from "@/app/fields";
import { FormDrawer } from "@/app/FormDrawer";
import { accounts, channels, customers, type Account, type Channel } from "@/app/master";
import { messages } from "@/app/messages";
import { Pager } from "@/app/Pager";
import { accountOptions, idOptions, loadingOf, nameOf, useList } from "@/app/queries";
import { testSend, type TestSendInput } from "@/app/testSend";
import { useDraft } from "@/app/useDraft";
import { totalOf, useCursorPage } from "@/app/usePage";
import { useSave } from "@/app/useSave";
import { Alert, Button, Combobox, EmptyState, Field, Form, formatTime, MessageStatePill, Msisdn, PageHeader, RadioGroup, shortId, Table, TableWrap, Textarea, Th } from "@/ui";
import { MessageDetail } from "./MessageDetail";
import { MAX_TEXT, testSendDraft, testSendInput } from "./testSendDraft";

/** 一条发出的测试：每次发送都写一条审计（动作 test_send、实体 message，内容是请求体，D94 第 8 条），列表就从审计读，刷新页面不丢。 */
interface Sent { id: string; at: string; actor: string; input: TestSendInput }

/** 状态自动刷新的时限：更早的测试只查一次，免得一页里卡在非终态的旧消息一直轮询。 */
const LIVE_MS = 30 * 60 * 1000;

const sentOf = (e: AuditEntry): Sent => ({ id: e.entity_id, at: e.created_at, actor: e.actor, input: e.after as TestSendInput });

/** 发送测试（决策 D94）：按通道或按账号发一条真实短信，结果在列表里跟到终态，点开看提交与回执。
 * 按通道不计费、按账号照常计费；都不给客户推回执，也不计入通道的指标与熔断。列表是所有人发过的测试，按审计记录倒序翻页（docs/04 第 11 节）。 */
export function TestSend() {
  const canWrite = useCanWrite(); // 只读账号从地址栏直接进来时：记录接口 403（D181），这里只给说明、不发请求
  const [composing, setComposing] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const pager = useCursorPage<number>("test-send");
  const list = useQuery({
    // 发出后随 useSave 的 invalidate 刷新
    queryKey: ["test-send-history", pager.limit, pager.cursor],
    queryFn: () => testSend.history({ limit: pager.limit, before_id: pager.cursor }),
    placeholderData: keepPreviousData,
    enabled: canWrite,
  });
  const total = useQuery({ queryKey: ["test-send-history", "count"], queryFn: testSend.historyCount, enabled: canWrite }); // 总数（决策 D182），发出后随列表一起刷新
  const paged = { ...pager.view(list.data ?? [], (r) => r.id), ...totalOf(total.data) };
  const channelList = useList("channels", channels);
  const accountList = useList("accounts", accounts);
  const target = (s: TestSendInput) => (s.target === "channel" ? `通道 ${nameOf(channelList.data, s.channel_id!)}` : `账号 ${nameOf(accountList.data, s.account_id!)}`);
  const description = "按通道测试直接发给该通道、绕开路由与熔断、不计费；按账号测试走正常路由并计费。都不推客户回执，不计入通道指标。";
  if (!canWrite) return <><PageHeader title="发送测试" description={description} /><Alert title="只有管理员能使用发送测试" /></>;
  return (
    <>
      <PageHeader title="发送测试" description={description}
        actions={<Button variant="primary" onClick={() => setComposing(true)}>发送测试</Button>} />
      {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      <TableWrap loading={loadingOf(list)}>
        {paged.rows.length ? (
          <>
          <Table>
            <thead><tr><Th w="time">时间</Th><Th w={140}>操作者</Th><Th w={220}>测试对象</Th><Th w={168}>号码</Th><Th w={120}>sender</Th><Th w="id">消息</Th><Th w="status">状态</Th><Th w={160}>错误</Th><Th /></tr></thead>
            <tbody>
              {paged.rows.map(sentOf).map((s) => <SentRow key={s.id} sent={s} target={target(s.input)} onOpen={() => setOpen(s.id)} />)}
            </tbody>
          </Table>
          <Pager {...paged} />
          </>
        ) : (
          !list.isPending && <EmptyState title="还没有发送">发出的测试列在这里，状态自动刷新到终态；点详情看提交与回执。</EmptyState>
        )}
      </TableWrap>
      {composing && <ComposeDrawer channels={channelList.data} accounts={accountList.data} onClose={() => setComposing(false)} onSent={() => setComposing(false)} />}
      {open && <MessageDetail id={open} channels={channelList.data} onClose={() => setOpen(null)} />}
    </>
  );
}

/** 一条测试的当前状态：消息由 worker 异步写入，刚发出时查不到（404）就稍后再查；到终态后停止刷新。超过 LIVE_MS 的只查一次，不重试也不刷新。
 * 与详情抽屉共用查询键。入队失败（503）的测试也有审计，消息一直查不到，显示"未找到消息"。 */
function SentRow({ sent, target, onOpen }: { sent: Sent; target: string; onOpen: () => void }) {
  const live = () => Date.now() - Date.parse(sent.at) <= LIVE_MS;
  const q = useQuery({
    queryKey: ["message", sent.id], queryFn: () => messages.get(sent.id),
    retry: (n, e) => e instanceof ApiError && e.status === 404 && n < 10 && live(), retryDelay: 1000,
    refetchInterval: (query) => (!isFinal(query.state.data?.message.status) && live() ? 3000 : false),
  });
  const m = q.data?.message;
  return (
    <tr>
      <td className="ax-mono">{formatTime(sent.at)}</td><td>{sent.actor}</td><td>{target}</td><td><Msisdn value={sent.input.to} /></td><td className="ax-mono">{sent.input.sender}</td>
      <td className="ax-mono">{shortId(sent.id)}</td><td>{m ? <MessageStatePill state={m.status} /> : <span className="ax-secondary">{q.error ? (q.error instanceof ApiError && q.error.status === 404 ? "未找到消息" : "查询失败") : "排队中"}</span>}</td>
      <td className="ax-mono">{m?.error_code ? `${m.error_code} ${m.error_name}` : ""}</td>
      <td><Button variant="ghost" size="sm" onClick={onOpen}>详情</Button></td>
    </tr>
  );
}

function isFinal(status: string | undefined) {
  return status === "DELIVERED" || status === "FAILED";
}

/** 发送表单。按通道用系统测试账号、不计费；按账号选一个客户账号、照常计费。 */
function ComposeDrawer({ channels, accounts: accountRows, onClose, onSent }: { channels?: Channel[]; accounts?: Account[]; onClose: () => void; onSent: () => void }) {
  const form = useDraft(testSendDraft());
  const customerList = useList("customers", customers);
  // 审计记在入队之前（D94 第 8 条），202 返回时列表重取就能看到这一条
  const { save, saving, error, reset } = useSave(testSend.send, { invalidate: ["messages", "test-send-history", "audit"], message: "已发出测试", onSaved: onSent });
  const submit = () => {
    const { body: next, errors } = testSendInput(form.draft);
    form.setErrors(errors);
    if (!next) return;
    reset();
    save(next);
  };
  const byChannel = form.draft.target === "channel";
  const chars = [...form.draft.text].length;
  return (
    <FormDrawer open title="发送测试" submitLabel="发送" saving={saving} error={error} onSubmit={submit} onClose={onClose}>
      <Form>
        <Field label="测试对象" required>
          <RadioGroup name="ts-target" label="测试对象" value={form.draft.target} onChange={(v) => form.set("target", v)} options={[
            { value: "channel", label: "通道", description: "用系统测试账号发，直接发给选中的通道，不计费" },
            { value: "account", label: "账号", description: "以选中账号的身份发，按它的账号路由绑定选路，照常计费" },
          ]} />
        </Field>
        {byChannel ? (
          <Field label="通道" required error={form.errors.channel} hint="不需要售价与采购价，sender 不受允许列表限制">
            <Combobox searchable block placeholder="选择通道" ariaLabel="通道" options={idOptions(channels)} value={form.draft.channel || null} invalid={!!form.errors.channel} onChange={(v) => form.set("channel", v)} />
          </Field>
        ) : (
          <Field label="账号" required error={form.errors.account}>
            <Combobox searchable block placeholder="选择账号" ariaLabel="账号" options={accountOptions(accountRows, customerList.data)} value={form.draft.account || null} invalid={!!form.errors.account} onChange={(v) => form.set("account", v)} />
          </Field>
        )}
        <TextField id="ts-to" label="号码" required mono placeholder="6281234567890" hint="E.164，带国家码" value={form.draft.to} error={form.errors.to} onChange={(v) => form.set("to", v)} />
        <TextField id="ts-sender" label="sender" required mono value={form.draft.sender} hint={byChannel ? "任意，不超过 20 个字节" : "账号设了允许列表时须在列表里"} error={form.errors.sender} onChange={(v) => form.set("sender", v)} />
        <Field label="正文" required htmlFor="ts-text" hint={`${chars} / ${MAX_TEXT} 字符`} error={form.errors.text}>
          <Textarea id="ts-text" rows={4} value={form.draft.text} invalid={!!form.errors.text} onChange={(e) => form.set("text", e.target.value)} />
        </Field>
      </Form>
    </FormDrawer>
  );
}
